import { Box, chakra, InputElement } from '@chakra-ui/react';
import {
  InvoiceApplicationFormDataInput,
  InvoiceApplicationFormDataRowInput,
  SelectionStateEnum,
} from '@monetize/types/app';
import {
  getCurrencyDecimals,
  getCurrencySymbol,
  toDateShort,
} from '@monetize/utils/core';
import { Controller, UseFormReturn } from 'react-hook-form';
import { getInvoiceDetailRoute } from '../../constants/routes';
import { roundNumberToDecimal } from '../../utils/numberUtils';
import {
  MCenter,
  MCheckbox,
  MCustomNumberInput,
  MDivider,
  MFlex,
  MFormField,
  MText,
} from '../Monetize';
import { ItemWithLinkInNewWindow } from '../Monetize/ItemWithLinkInNewWindow';
import { MCurrency } from '../Monetize/MCurrency';
import { useTransactionApplicationRow } from './useTransactionApplicationRow';

interface RefundApplicationTableRowProps {
  index: number;
  formState: UseFormReturn<InvoiceApplicationFormDataInput>;
  row: InvoiceApplicationFormDataRowInput;
  isDisabled?: boolean;
  /**
   * Currently unapplied amount of payment or credit
   */
  availableAmount: number;
}

/**
 * Row variant used by the refund flow. Shares its selection logic with
 * {@link TransactionApplicationTableRow} via {@link useTransactionApplicationRow}
 * but omits the Amount Due and Status columns, which are not relevant when
 * unapplying a payment to refund it.
 */
export const RefundApplicationTableRow = ({
  index,
  formState,
  row,
  isDisabled,
  availableAmount,
}: RefundApplicationTableRowProps) => {
  const { control } = formState;
  const { amountAppliedFromOtherTransactables, invoice } = row;
  const { amount, currency } = invoice;
  const { handleToggleCheckAll } = useTransactionApplicationRow({
    index,
    formState,
    row,
    availableAmount,
  });

  return (
    <>
      <MCenter role="cell">
        <Controller
          name={`rows.${index}.checkedState`}
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <MCheckbox
              isChecked={value === SelectionStateEnum.ALL}
              isIndeterminate={value === SelectionStateEnum.SOME}
              isDisabled={isDisabled}
              id={`rows.${index}.checkedState`}
              onChange={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleToggleCheckAll();
              }}
              {...rest}
            />
          )}
        />
      </MCenter>
      <MFlex role="cell" align="center">
        <chakra.label
          htmlFor={`rows.${index}.checkedState`}
          fontWeight="400"
          m="0"
        >
          <ItemWithLinkInNewWindow url={getInvoiceDetailRoute(invoice.id)}>
            <MText>{invoice.invoiceNumber ?? invoice.id}</MText>
          </ItemWithLinkInNewWindow>
          <MText fontSize="xs">Due {toDateShort(invoice.dueDate)}</MText>
        </chakra.label>
      </MFlex>

      <MFlex role="cell" align="center" justify="flex-end">
        <MCurrency amount={amount} options={{ currency }} />
      </MFlex>

      <MFlex role="cell" align="center" justify="center">
        <MCurrency
          amount={roundNumberToDecimal(amountAppliedFromOtherTransactables)}
          options={{ currency }}
        />
      </MFlex>

      <MFormField role="cell" error={`rows.${index}.appliedAmount`}>
        <Controller
          name={`rows.${index}.appliedAmount`}
          control={control}
          render={({ field: { value, onChange, ...rest } }) => (
            <Box position="relative">
              <InputElement placement="start" w="4" pl="2">
                {getCurrencySymbol(invoice.currency)}
              </InputElement>
              <MCustomNumberInput
                value={value ?? 0}
                inputMode="decimal"
                placeholder={`0.00`}
                precision={getCurrencyDecimals(currency)}
                isDisabled={isDisabled}
                styleProps={{
                  pl: 5,
                  pr: 2,
                  textAlign: 'right',
                }}
                onChange={(valueAsString, valueAsNumber) =>
                  onChange(valueAsString)
                }
                {...rest}
              />
            </Box>
          )}
        />
      </MFormField>

      <MDivider gridColumn="1/-1" />
    </>
  );
};
